/**
 * Format boundary detection and cursor navigation.
 *
 * Detects when the cursor is at the boundary of a formatting element
 * (e.g., the start or end of <strong>), and provides utilities to
 * move the cursor outside the formatted span.
 */

import { MAX_PARENT_WALK_DEPTH } from "./constants";

const FORMATTING_TAGS = new Set([
	"STRONG",
	"EM",
	"U",
	"S",
	"A",
	"SUB",
	"SUP",
]);

interface BoundaryInfo {
	isAtBoundary: boolean;
	formattedElement: HTMLElement | null;
	side: "start" | "end" | null;
}

const NO_BOUNDARY: BoundaryInfo = {
	isAtBoundary: false,
	formattedElement: null,
	side: null,
};

/**
 * Find the outermost formatting ancestor of a node that is at the boundary.
 * Stops at the editor root (fr-view).
 */
const findOutermostFormattingAncestor = (
	node: Node,
	side: "start" | "end",
): HTMLElement | null => {
	let outermost: HTMLElement | null = null;
	let current: Node | null =
		node instanceof HTMLElement ? node : node.parentElement;
	let depth = 0;

	while (current instanceof HTMLElement && depth < MAX_PARENT_WALK_DEPTH) {
		depth++;
		if (current.classList.contains("fr-view")) break;

		if (FORMATTING_TAGS.has(current.tagName)) {
			// Verify this ancestor is also at the boundary
			if (side === "start") {
				if (getFirstTextDescendant(current)) {
					outermost = current;
				}
			} else {
				if (getLastTextDescendant(current)) {
					outermost = current;
				}
			}
		}

		current = current.parentElement;
	}

	return outermost;
};

const getFirstTextDescendant = (node: Node): Text | null => {
	if (node.nodeType === Node.TEXT_NODE) return node as Text;
	for (const child of Array.from(node.childNodes)) {
		const found = getFirstTextDescendant(child);
		if (found) return found;
	}
	return null;
};

const getLastTextDescendant = (node: Node): Text | null => {
	if (node.nodeType === Node.TEXT_NODE) return node as Text;
	for (let i = node.childNodes.length - 1; i >= 0; i--) {
		const found = getLastTextDescendant(node.childNodes[i]);
		if (found) return found;
	}
	return null;
};

/**
 * Detect if the collapsed cursor is at the boundary of a formatting element.
 */
export const detectBoundary = (sel: Selection): BoundaryInfo => {
	if (!sel.rangeCount || !sel.isCollapsed) return NO_BOUNDARY;

	const range = sel.getRangeAt(0);
	const { startContainer, startOffset } = range;

	// Check if at the START of a text node (offset 0)
	if (
		startContainer.nodeType === Node.TEXT_NODE &&
		startOffset === 0
	) {
		const parent = startContainer.parentElement;
		if (parent && FORMATTING_TAGS.has(parent.tagName)) {
			const firstText = getFirstTextDescendant(parent);
			if (firstText === startContainer) {
				const outermost = findOutermostFormattingAncestor(parent, "start") ?? parent;
				return {
					isAtBoundary: true,
					formattedElement: outermost,
					side: "start",
				};
			}
		}
	}

	// Check if at the END of a text node
	if (
		startContainer.nodeType === Node.TEXT_NODE &&
		startOffset === startContainer.textContent?.length
	) {
		const parent = startContainer.parentElement;
		if (parent && FORMATTING_TAGS.has(parent.tagName)) {
			const lastText = getLastTextDescendant(parent);
			if (lastText === startContainer) {
				const outermost = findOutermostFormattingAncestor(parent, "end") ?? parent;
				return {
					isAtBoundary: true,
					formattedElement: outermost,
					side: "end",
				};
			}
		}
	}

	return NO_BOUNDARY;
};

/**
 * Move the cursor outside the formatted element based on the boundary side.
 */
export const exitFormattedElement = (
	sel: Selection,
	boundary: BoundaryInfo,
): void => {
	if (!boundary.isAtBoundary || !boundary.formattedElement) return;

	const range = document.createRange();

	if (boundary.side === "end") {
		range.setStartAfter(boundary.formattedElement);
	} else if (boundary.side === "start") {
		range.setStartBefore(boundary.formattedElement);
	}

	range.collapse(true);
	sel.removeAllRanges();
	sel.addRange(range);
};
